import type { ScheduleOrder } from './filterEvents'
import {
  admitByWatermark,
  laggingStream,
  resolveWatermark,
  type StreamFrontier,
} from './scheduleWatermark'
import type { SnEvent } from './types'

/** A game as the schedule list consumes it — only `starts_at` matters here. */
interface TimedGame {
  starts_at?: string | null
}

/** Which stream to advance next, or null once nothing is left to fetch. */
export type NextStream = 'games' | 'events' | 'both' | null

export interface CombineScheduleStreamsArgs<TGame extends TimedGame> {
  games: TGame[]
  gamesFrontier: StreamFrontier
  events: SnEvent[]
  eventsFrontier: StreamFrontier
  order?: ScheduleOrder
  /**
   * Skip the watermark entirely — for a BOUNDED window (a calendar month, week
   * or day) that draws all at once and has nothing to scroll.
   */
  bypass?: boolean
}

export interface CombineScheduleStreamsResult<TGame extends TimedGame> {
  visibleGames: TGame[]
  visibleEvents: SnEvent[]
  /** True while either stream has pages left. */
  hasNextPage: boolean
  nextStream: NextStream
}

/**
 * Reduces two independently paginated streams to the rows that can safely be
 * rendered, plus which stream to fetch next.
 *
 * Pure, and separate from `useScheduleStream` so the rule is testable without a
 * renderer — the hook adds only the stall guard around this.
 *
 * ## Why games can be held back too
 *
 * The clamp this generalizes only ever withheld EVENTS, because events arrived
 * complete and could not lag. Once both sides paginate, either can be the one
 * that has not reached far enough — a team with dense practices and sparse
 * games gets there routinely. Rendering a game past the event frontier would
 * put it above events still to load, and those events would then appear above
 * a row the user has already scrolled past. Holding it costs one more page;
 * showing it costs the list's credibility.
 *
 * In the common case this never fires: events are far sparser than games, so
 * the event stream exhausts early, its frontier stops constraining, and the
 * behaviour is exactly the games-only boundary it replaces.
 */
export const combineScheduleStreams = <TGame extends TimedGame>({
  games,
  gamesFrontier,
  events,
  eventsFrontier,
  order = 'asc',
  bypass = false,
}: CombineScheduleStreamsArgs<TGame>): CombineScheduleStreamsResult<TGame> => {
  // A bounded calendar window draws a month, week or day all at once and has
  // nothing to scroll, so a partially loaded window is simply wrong. It loads
  // to completion instead — the same call the games list already makes.
  const watermark = bypass
    ? ({ kind: 'open' } as const)
    : resolveWatermark({ games: gamesFrontier, events: eventsFrontier, order })

  const hasNextPage = gamesFrontier.hasMore || eventsFrontier.hasMore

  const nextStream: NextStream = bypass
    ? hasNextPage
      ? 'both'
      : null
    : laggingStream({ games: gamesFrontier, events: eventsFrontier, order })

  return {
    visibleGames: admitByWatermark(
      games,
      (game) => game?.starts_at,
      watermark,
      order
    ),
    visibleEvents: admitByWatermark(
      events,
      (event) => event?.starts_at,
      watermark,
      order
    ),
    hasNextPage,
    nextStream,
  }
}
